import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';
import { getApplicationForms } from '@/api/Rover/ApplicationForm';
import UnAuthorizedMessage from '@/components/Permission/UnauthorizedMessage';
import Badge from '@/components/UI/Badge';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
import { usePermission } from '@/hooks/usePermission';
import { workspaceSelector } from '@/store';
import { notifyUser } from '@/store/Toast';
import { EPermission } from '@/types/Permission';

export const ApplicationFormsList = () => {
  const dispatch = useDispatch();
  const { currentWorkspace } = useSelector(workspaceSelector);
  const { hasPermission } = usePermission();

  const fetchApplicationForms = useQuery(
    [RoverReactQueryKey.GET_APPLICATION_FORMS, currentWorkspace?.id],
    () =>
      getApplicationForms({
        params: { workspaceId: currentWorkspace?.id },
      })
    // {
    //   enabled: !!currentWorkspace?.id,
    // }
  );

  const applicationForms = fetchApplicationForms?.data?.data;

  return (
    <div className='mt-4 flex flex-col'>
      <div className='-my-2'>
        <div className='inline-block w-full py-2 align-middle'>
          <div className='overflow-hidden'>
            <table className='w-full border-separate' style={{ borderSpacing: 0 }}>
              <thead className='bg-gray-50'>
                <tr>
                  <th
                    scope='col'
                    className='sticky -top-5 z-10 border-b border-gray-300 bg-gray-50/75 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur sm:pl-6 lg:pl-8'
                  >
                    Name
                  </th>
                  <th
                    scope='col'
                    className='sticky -top-5 z-10 border-b border-gray-300 bg-gray-50/75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur'
                  >
                    Description
                  </th>
                  <th
                    scope='col'
                    className='sticky -top-5 z-10 hidden border-b border-gray-300 bg-gray-50/75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur lg:table-cell'
                  >
                    Status
                  </th>
                </tr>
              </thead>
              {hasPermission([
                EPermission.LIST_APPLICATION_FORMS,
                EPermission.LIST_APPLICATION_FORMS,
                EPermission.READ_APPLICATION_FORM_DASHBOARD,
                EPermission.LIST_APPLICATION_FORM_RESOURCES,
              ]) && (
                <tbody>
                  {applicationForms?.map((form: any) => (
                    <tr key={form.id} className='hover:bg-gray-50'>
                      {hasPermission([
                        EPermission.READ_APPLICATION_FORM,
                        EPermission.LIST_APPLICATION_FORMS,
                        EPermission.READ_APPLICATION_FORM_DASHBOARD,
                        EPermission.LIST_APPLICATION_FORM_RESOURCES,
                      ]) && (
                        <td className='whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 lg:pl-8  hover:underline cursor-pointer'>
                          <Link to={form.id}>{form.name}</Link>
                        </td>
                      )}
                      {!hasPermission([
                        EPermission.READ_APPLICATION_FORM,
                        EPermission.LIST_APPLICATION_FORMS,
                        EPermission.READ_APPLICATION_FORM_DASHBOARD,
                        EPermission.LIST_APPLICATION_FORM_RESOURCES,
                      ]) && (
                        <td
                          className='whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 lg:pl-8 hover:underline cursor-pointer'
                          onClick={() => {
                            dispatch(
                              notifyUser({
                                showToast: true,
                                toastMessage: 'You do not have permission to read application form details.',
                                toastType: 'failed',
                              })
                            );
                          }}
                        >
                          {form.name}
                        </td>
                      )}
                      <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500'>{form?.description}</td>
                      <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500 hidden lg:table-cell'>
                        {form.active ? <Badge text='ACTIVE' status='ACTIVE' /> : <Badge text='INACTIVE' status='INACTIVE' />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
          {applicationForms?.length === 0 && (
            <section className='w-full flex justify-center items-center h-screen/2 bg-white'>
              <div className='max-w-lg text-center'>
                <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                  <DocumentTextIcon className='h-8 w-8' />
                  <h1>No Application Forms</h1>
                </div>
                <p>
                  There are no application forms to display in{' '}
                  {currentWorkspace ? <span className='text-primaryColor-normal'>{currentWorkspace?.name}</span> : 'all'} workspace.
                </p>
              </div>
            </section>
          )}
          {!hasPermission([
            EPermission.LIST_APPLICATION_FORMS,
            EPermission.LIST_APPLICATION_FORMS,
            EPermission.READ_APPLICATION_FORM_DASHBOARD,
            EPermission.LIST_APPLICATION_FORM_RESOURCES,
          ]) && <UnAuthorizedMessage title='Unauthorized' message='You do not have permission to list the application forms.' />}
        </div>
      </div>
    </div>
  );
};
